import { FileRejection, useDropzone } from 'react-dropzone';
import {
  Control,
  FieldPathByValue,
  FieldValues,
  useController,
  UseFormClearErrors,
  UseFormSetError,
} from 'react-hook-form';
import { twMerge } from 'tailwind-merge';
import { v4 as uuidv4 } from 'uuid';

import { CloudUploadIcon, CrossIcon, InfoCircle, UploadIcon } from '../../assets/icons/index';
import { BackendRoute } from '../../config/index';
import { ApiService } from '../../services/index';
import { zodFileSchema, ZodFileSchema } from '../../types/general.types';
import Button from '../button/index';
import Loader from '../loader/index';

type Props<
  TFieldValues extends FieldValues,
  TPath extends FieldPathByValue<TFieldValues, unknown>,
> = {
  acceptFileFormats?: { [key: string]: string[] };
  clearErrors: UseFormClearErrors<TFieldValues>;
  control: Control<TFieldValues>;
  description?: string;
  maxUploads?: number;
  name: TPath;
  placeholder?: string;
  schema: ReturnType<typeof zodFileSchema>;
  setError: UseFormSetError<TFieldValues>;
};

export const FormDocumentDropZone = <
  TFieldValues extends FieldValues,
  TPath extends FieldPathByValue<TFieldValues, unknown>,
>({
  control,
  name,
  setError,
  maxUploads = 1,
  clearErrors,
  schema,
  placeholder,
  description,
  acceptFileFormats,
}: Props<TFieldValues, TPath>) => {
  const {
    fieldState: { error },
    field: { onChange, value: rawValue },
  } = useController({ control, name });

  const value = rawValue as ZodFileSchema | ZodFileSchema[];

  const notArray = (value: ZodFileSchema | ZodFileSchema[]): value is ZodFileSchema =>
    !Array.isArray(value);

  const isArray = maxUploads > 1;

  const formFiles = (Array.isArray(value) ? value : value ? [value] : []) as ZodFileSchema[];

  const isReachedUploadMaximum = formFiles.length >= maxUploads;

  const onDrop = async (acceptedFiles: File[], fileRejections: FileRejection[]) => {
    if (fileRejections.length) {
      fileRejections.forEach(({ file, errors }) => {
        for (const { code, message } of errors) {
          if (code === 'file-invalid-type')
            return setError(name, {
              type: 'manual',
              message: `Invalid file type ${file?.type}`,
            });

          return setError(name, {
            type: 'manual',
            message,
          });
        }
      });
      return;
    }

    if (isReachedUploadMaximum) return;
    const filesToUpload = acceptedFiles.slice(0, 20 - formFiles.length);

    filesToUpload.forEach(async (file) => {
      const files = formFiles;
      const { callback, abort } = ApiService.uploadFile();
      const id = uuidv4();

      const payload: ZodFileSchema = { doc: file, docLink: '', loadProgress: 1, abort, id };
      const { error, success } = schema.safeParse(isArray ? [payload] : payload);

      if (!success) return setError(name, error.issues[0]);

      if (success) {
        clearErrors(name);
        files.push(payload);
      }

      onChange(isArray ? files : payload);

      const link = await callback({ endpoint: BackendRoute.IMAGE_UPLOAD }, ['file', file], {
        onUploadProgress: (n) => {
          onChange(
            isArray
              ? files.map((f) => (f.id === id ? { ...f, loadProgress: n } : f))
              : Object.assign(files[0], { loadProgress: n }),
          );
        },
        onAboard: () => {
          onChange(isArray ? files.filter((f) => f.id !== id) : undefined);
        },
      });

      if (!link) return;

      onChange(
        isArray
          ? files.map((f) => (f.id === id ? { ...f, loadProgress: 100, docLink: link } : f))
          : Object.assign(files[0], { loadProgress: 100, docLink: link }),
      );
    });
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    multiple: true,
    disabled: isReachedUploadMaximum,
    onDropRejected: () =>
      setError(name, {
        message: 'File format not supported',
        type: 'validate',
      }),
    accept: acceptFileFormats,
  });

  const handleDeleteFile = (id?: string) => {
    const current = id ? formFiles.find((file) => file.id === id) : formFiles[0];

    if (current && !current.docLink) current.abort();

    const currentData = isArray ? formFiles.filter((file) => file.id !== id) : undefined;

    onChange(currentData);

    if (currentData?.length === 0 || !currentData) return clearErrors(name);
  };

  if (notArray(value) && value)
    return (
      <div className="flex flex-col gap-3">
        <span className="text-xs font-[450] text-gray-500">{placeholder}</span>
        <div className="flex h-[72px] w-full items-center gap-4 rounded-lg bg-brand-50 p-4">
          {!value.docLink ? (
            <Loader className="w-fit" iconClassName="size-8" />
          ) : (
            <img
              alt={value.doc.name ?? 'Preview'}
              className="size-8 shrink-0 rounded-full object-cover"
              src={value.docLink}
            />
          )}
          <div className="flex w-full flex-col gap-1 text-sm font-[450] text-gray-700">
            <span className="w-[300px] truncate">{value.doc.name}</span>
            <a
              className="text-brand-700 underline"
              href={value.docLink}
              rel="noreferrer"
              target="_blank"
            >
              View
            </a>
          </div>
          <Button
            className="size-4 rounded-md p-3 hover:bg-brand-25"
            onClick={() => handleDeleteFile()}
            styleType="NONE"
          >
            <CrossIcon />
          </Button>
        </div>
      </div>
    );

  return (
    <div className="flex flex-col gap-3">
      <span className="text-xs font-[450] text-gray-500">{placeholder}</span>

      <input
        {...getInputProps()}
        accept=".pdf, .doc, .docx, .xlsx, .xls, .csv, .png, .jpeg, .jpg"
        className="hidden"
        multiple
        type="file"
      />
      <label
        {...getRootProps()}
        className={twMerge(
          'relative flex h-[72px] w-full items-center gap-6 rounded-lg border border-dashed border-gray-200 p-6',
          isReachedUploadMaximum ? 'cursor-auto' : 'cursor-pointer',
          error && 'h-fit p-4',
        )}
      >
        {error ? (
          <InfoCircle className="size-14" thinIconVariant />
        ) : (
          <CloudUploadIcon className="size-10" />
        )}
        <div className="flex flex-col gap-2">
          <div className="flex">
            {error ? (
              <span className="text-sm font-[550] text-fireside-600">
                {error.message?.toString()}
              </span>
            ) : (
              <p className="text-sm font-medium text-gray-700">
                <span className="underline">Click to upload</span> or drag and drop
              </p>
            )}
          </div>

          <div className="flex flex-col gap-1 text-label-sm font-[450] text-gray-500">
            <span>{description}</span>
          </div>
          {error && (
            <span className="flex h-9 w-fit items-center gap-1 rounded border border-gray-100 bg-gray-25 px-3 py-[6px] text-sm font-[450] text-gray-700">
              <UploadIcon />
              Upload again
            </span>
          )}
        </div>
      </label>
    </div>
  );
};
